import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	BrandingSettingsListItemModel,
	BrandingSettingsListModel,
	deleteBranding,
	list
} from "client/Api/Branding/branding";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPage } from "../../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	FloatingActionButton,
	Icon,
	Pagination,
	SearchInputForm,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog
} from "client/Components";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useSelection,
	useSortHandler,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { contains } from "client/Data/searching";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { EMPTY_ARRAY } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter,
	route
} from "client/Navigation";
import { EditBrandingSettingFormRouteParams } from "../EditBranding/BrandingSettingForm";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type BrandingOverviewProps = {
	onNavigate: NavigationCallback;
};

const {
	Row,
	Cell
} = TableView;

const useBrandingOverviewDataSource = createLocalDataSource<BrandingSettingsListItemModel>()
		.setConfiguration({
			search: {
				doesMatchItem: (item, searchItem) => contains(item.brandingName, searchItem)
			},
			hasPagination: true
	})
	.getHook();

export const BrandingOverview = (props: BrandingOverviewProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [allItems, setAllItems] = useState<readonly BrandingSettingsListItemModel[]>(EMPTY_ARRAY);
	const [selectedBrandingItems, setSelectedBrandingItems] = useState<readonly BrandingSettingsListItemModel[]>([]);
	const [sortingState, setSortingState] = useState<DataSourceSortableState<BrandingSettingsListItemModel>>({
		direction: DataSourceSortDirection.Ascending,
		propertyName: "brandingName"
	});

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const alertManager: AlertManager = useAlerts();

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	useActiveSettingsMenuItem(SettingsPageKind.Branding);

	const {
		sortingFor
	} = useSortHandler(sortingState, setSortingState);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);
				try {
					const model: BrandingSettingsListModel = await list(signal)
						.catch(handlePageDataLoadingError);

					setAllItems(model.brandingSettingsItems);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}

				setIsBusy(false);
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const {
		currentItems,
		pagination
	} = useBrandingOverviewDataSource(
		allItems,
		{
			searchTerm,
			pagination: {
				currentPageNumber
			},
			sorting: sortingState
		}
	);

	const { selectionFor, multiSelection } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selectedBrandingItems,
			setSelectedItems: setSelectedBrandingItems,
		}
	);

	const addNewBranding = useCallback(
		(): void => {
			onNavigate(brandingRouting.new);
		},
		[onNavigate]
	);

	const editBranding = useCallback(
		(item: BrandingSettingsListItemModel): void => {
			onNavigate(brandingRouting.edit({ id: `${item.id}` }));
		},
		[
			onNavigate
		]
	);

	const deleteHandler = useCallback(
		async (brandingItems: readonly BrandingSettingsListItemModel[]): Promise<void> => {
			try {
				const result: TryResult<void> = await openConfirmationDialog({
					title: brandingItems.length === 1
						? localize("_AreYouSureYouWantToDeleteThisBranding")
						: localize("_AreYouSureYouWantToDeleteTheseBrandings"),
					confirmText: localize("_Delete"),
					trackingId:"branding-delete-confirmation-dialog",
				});

				if (!result.wasSuccessful) {
					return;
				}

				setIsBusy(true);
				await deleteBranding(brandingItems.map(a => a.id));
				setAllItems(
					allItems => {
						return allItems.filter(
							item => !brandingItems
							.some(
								deletedItem => deletedItem.id === item.id
							)
						);
					}
				);
				setSelectedBrandingItems([]);
				setIsBusy(false);
			} catch(e) {
				setIsBusy(false);
				console.error(e);
				alertManager.showError(localize("_ErrorDeletingBrandings"));
			}
		},
		[alertManager, openConfirmationDialog]
	);

	const actionContext = useMemo(
		(): BrandingActionContext => ({
			selection: selectedBrandingItems,
			isMobile
		}),
		[isMobile, selectedBrandingItems]
	);

	const selectionActions: readonly BrandingAction[] = useMemo(
		() => getSelectionActions({
			deselectHandler: () => setSelectedBrandingItems([])
		}),
		[]
	);

	const contextualActions: readonly BrandingAction[] = useMemo(
		() => listContextualActions({
			editBrandingHandler: editBranding,
			deleteBrandingHandler: deleteHandler
		}),
		[editBranding, deleteHandler]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<BrandingActionContext> => {
			return getMainAction(addNewBranding);
		},
		[addNewBranding]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly BrandingAction[] => {
			return buildActionsForTopBar<BrandingActionContext, BrandingAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			hasSelection,
			contextualActions,
			isResponsive,
			mainAction,
			selectionActions
		]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: localize("_NoBrandingSetsYet"),
				message: localize("_ThereAreNoBrandingSetsYet")
			};
		},
		[searchTerm]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={actionContext}
				title={localize("_Branding")}
				hasSelection={hasSelection}
			>
				<SettingsPage.Section
					height="stretch"
				>
					<SettingsPage.SearchSection>
						<SearchInputForm
							onChange={setSearchTerm}
							trackingId="search"
							icon="filter"
							height="large"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[localize("_Name")]}
								/>
							)}
						/>
					</SettingsPage.SearchSection>
					<SettingsPage.Section height="stretch">
						<TableView
							multiSelectable={multiSelection}
							items={currentItems}
							itemKey="id"
							height="stretch"
							columns={[
								{
									title: localize("_Name"),
									sortable: sortingFor("brandingName"),
									trackingId: "branding-name"
								},
								{
									title: localize("_DefaultBranding"),
									sortable: sortingFor("isDefault"),
									trackingId: "is-default"
								}
							]}
							trackingId="branding-settings"
							noResultConfig={noResultConfig}
						>
							{i => (
								<Row
									trackingId={i.id.toString()}
									selectable={selectionFor(i)}
									onExecute={() => editBranding(i)}
								>
									<Cell shouldExecuteOnClick>
										{i.brandingName}
									</Cell>
									<Cell>
										{i.isDefault &&
											<Icon value="check" size="large" />
										}
									</Cell>
								</Row>
							)}
						</TableView>
						<Pagination
							{...pagination}
							onPageNumberChanged={setCurrentPageNumber}
							trackingId="pagination"
						/>
					</SettingsPage.Section>
				</SettingsPage.Section>
				{mainAction.canExecute(actionContext) && (
					<>
						<div className="preserve-space-for-mobile-plus-button"/>
						<FloatingActionButton
							onClick={() => mainAction.execute(actionContext)}
							icon="plus"
							trackingId="new-button"
						/>
					</>
				)}
				<AiAssistantFabSpacing />
			</SettingsPage>
			{confirmationDialog}
		</>
	);
};
BrandingOverview.displayNames = "BrandingOverview";

export const BrandingOverviewForRouter = connectToRouter(
	BrandingOverview,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type BrandingActionContext = {
	selection: readonly BrandingSettingsListItemModel[];
	isMobile: boolean;
};

type BrandingAction = ToolbarAction<BrandingActionContext>;

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(handlers: SelectionActionsHandlers): readonly BrandingAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<BrandingActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "new",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newHandler(),
		icon: "plus",
		isPrimary: true
	};
}

type BrandingContextualActionsHandlers = {
	editBrandingHandler(item: BrandingSettingsListItemModel): void;
	deleteBrandingHandler(items: readonly BrandingSettingsListItemModel[]): void;
};

function listContextualActions(
	handlers: BrandingContextualActionsHandlers
): readonly BrandingAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => handlers.editBrandingHandler(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => handlers.deleteBrandingHandler(c.selection),
			icon: "trash-can"
		}
	];
}

export const brandingRouting = {
	overview: "/Branding",
	new: "/Branding/Add",
	edit: route<EditBrandingSettingFormRouteParams>`/Branding/Edit/${"id"}`
};